<template>
  <div :class="['punch-panel', { 'punch-panel--light': light, 'punch-panel--inline': inline, 'punch-panel--confirming': !!pendingAction }]">
    <header
      v-if="!inline"
      class="punch-panel__header">
      <span class="punch-panel__header-title">
        <q-icon
          :name="symRoundedSchedule"
          size="18px"
          class="punch-panel__header-icon" />
        <span class="punch-panel__title">{{ t('composites.PunchComponent.status') }}</span>
      </span>
      <q-badge
        outline
        color="primary"
        :label="dateLabel"
        class="punch-panel__badge" />
      <q-btn
        flat
        dense
        round
        size="sm"
        :icon="symRoundedClose"
        class="punch-panel__close"
        @click.stop="emit('close')">
        <q-tooltip>Fermer</q-tooltip>
      </q-btn>
    </header>

    <div class="punch-panel__body">
      <div
        v-if="!inline"
        class="punch-panel__status-row">
        <StatusPill :color="statusPillColor">
          <q-icon
            :name="statusIcon"
            size="12px"
            class="q-mr-xs" />
          <span>{{ statusLabel }}</span>
        </StatusPill>
        <span
          v-if="statusSince"
          class="punch-panel__status-detail punch-panel__status-detail--since">{{ statusSince }}</span>
        <span
          v-if="statusElapsed"
          class="punch-panel__status-detail punch-panel__status-detail--elapsed">{{ statusElapsed }}</span>
      </div>

      <div
        v-if="availableActions.length"
        class="punch-panel__actions">
        <q-btn
          v-for="action in availableActions"
          :key="action.type"
          flat
          no-caps
          :icon="action.icon"
          :label="action.label"
          :disable="loading"
          :class="['punch-panel__action-btn', `punch-panel__action-btn--${action.color}`]"
          @click="requestConfirm(action)" />
      </div>

      <dl
        v-if="hasAnySummary"
        class="punch-panel__summary-list">
        <div
          v-if="billableTime"
          class="punch-panel__summary-row">
          <dt class="punch-panel__summary-label">{{ t('composites.PunchComponent.billable_time') }}</dt>
          <dd class="punch-panel__summary-value punch-panel__summary-value--accent">{{ compact(billableTime) }}</dd>
        </div>
        <template v-if="totalBreak">
          <div class="punch-panel__summary-row">
            <dt class="punch-panel__summary-label">{{ t('composites.PunchComponent.breaks_label') }}</dt>
            <dd class="punch-panel__summary-value">{{ compact(totalBreak) }}</dd>
          </div>
          <div
            v-for="(b, i) in breaks"
            :key="`${b.start}-${i}`"
            class="punch-panel__summary-row punch-panel__summary-row--sub">
            <dt class="punch-panel__summary-label punch-panel__summary-label--sub">#{{ i + 1 }}</dt>
            <span class="punch-panel__summary-range">{{ formatHM(b.start) }}–{{ b.end ? formatHM(b.end) : '…' }}</span>
            <dd class="punch-panel__summary-value punch-panel__summary-value--sub">{{ compact(b.duration) }}</dd>
          </div>
        </template>
        <div
          v-if="rawWorkTime"
          class="punch-panel__summary-row">
          <dt class="punch-panel__summary-label">{{ t('composites.PunchComponent.raw_work_time') }}</dt>
          <dd class="punch-panel__summary-value">{{ compact(rawWorkTime) }}</dd>
        </div>
      </dl>

      <Transition name="punch-panel-confirm">
        <div
          v-if="pendingAction"
          class="punch-panel__confirm-overlay"
          @click.self="cancelConfirm">
          <div :class="['punch-panel__confirm-card', `punch-panel__confirm-card--${pendingAction.action.color}`]">
            <div class="punch-panel__confirm-title">
              <q-icon
                :name="pendingAction.action.icon"
                size="20px"
                class="punch-panel__confirm-icon" />
              {{ pendingAction.action.confirmLabel }}
            </div>
            <q-input
              v-if="!confirmingNow"
              v-model="confirmComment"
              dense
              filled
              :placeholder="t('composites.PunchComponent.dialog.add_comment')"
              class="punch-panel__confirm-input" />
            <div
              v-if="confirmingNow"
              class="punch-panel__confirm-spinner">
              <q-spinner
                :color="pendingAction.action.color"
                size="36px" />
            </div>
            <div
              v-else
              class="punch-panel__confirm-buttons">
              <q-btn
                flat
                no-caps
                label="Annuler"
                class="punch-panel__confirm-btn punch-panel__confirm-btn--cancel"
                @click="cancelConfirm" />
              <q-btn
                outline
                no-caps
                :color="pendingAction.action.color"
                label="Confirmer"
                class="punch-panel__confirm-btn punch-panel__confirm-btn--ok"
                @click="confirmAction" />
            </div>
          </div>
        </div>
      </Transition>

      <footer class="punch-panel__footer">
        <span class="punch-panel__footer-text">
          {{ t('composites.PunchComponent.captions.last_update', { time: lastUpdateText }) }}
        </span>
        <q-btn
          flat
          dense
          round
          size="sm"
          :icon="symRoundedRefresh"
          :loading="loading"
          class="punch-panel__refresh"
          @click.stop="emit('reload')">
          <q-tooltip>Rafraîchir</q-tooltip>
        </q-btn>
      </footer>
    </div>
  </div>
</template>

<script setup lang="ts">
import { symRoundedClose, symRoundedRefresh, symRoundedSchedule } from '@quasar/extras/material-symbols-rounded'
import StatusPill from 'src/CrudManager/Core/composites/Ui/StatusPill/StatusPill.vue'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

type PunchType = 'IN' | 'OUT' | 'BREAK_START' | 'BREAK_END'

interface PunchAction {
  type: PunchType
  color: string
  icon: string
  label: string
  confirmLabel: string
}

interface BreakItem {
  start: string
  end: string | null
  duration: string
  durationSeconds: number
}

const props = withDefaults(defineProps<{
  light: boolean
  inline?: boolean
  statusIcon: string
  statusLabel: string
  statusPillColor: string
  statusSince: string
  statusElapsed: string
  availableActions: PunchAction[]
  rawWorkTime: string
  billableTime: string
  totalBreak: string
  plannedTravelTime: string
  breaks: BreakItem[]
  loading: boolean
  dateLabel: string
  lastUpdateText: string
}>(), {
  inline: false,
})

const emit = defineEmits<{
  close: []
  reload: []
  punch: [type: PunchType, comment: string]
  requestConfirm: [action: PunchAction]
}>()

const { t } = useI18n()

const hasAnySummary = computed(() => Boolean(props.rawWorkTime || props.billableTime || props.totalBreak || props.plannedTravelTime))

const compact = (s: string) => s.replace(/\s+/g, '')

const formatHM = (iso: string) => {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`
}

const pendingAction = ref<{ action: PunchAction } | null>(null)
const confirmingNow = ref(false)
const confirmComment = ref('')

const requestConfirm = (action: PunchAction) => {
  confirmComment.value = ''
  if (props.inline) {
    emit('requestConfirm', action)
    return
  }
  pendingAction.value = { action }
}

const cancelConfirm = () => {
  if (confirmingNow.value) return
  pendingAction.value = null
  confirmComment.value = ''
}

const confirmAction = () => {
  if (!pendingAction.value || confirmingNow.value) return
  const { action } = pendingAction.value
  confirmingNow.value = true
  emit('punch', action.type, confirmComment.value)
}

watch(() => props.loading, (now, prev) => {
  if (prev && !now && confirmingNow.value) {
    confirmingNow.value = false
    pendingAction.value = null
  }
})
</script>

<style lang="scss">
.punch-panel {
  position: relative;
  min-width: 340px;
  max-width: 420px;
  background: #14181e;
  color: #e7eaee;
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 12px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.55), 0 1px 0 rgba(255, 255, 255, 0.04) inset;
  overflow: hidden;
  display: flex;
  flex-direction: column;

  &--inline {
    min-width: 0;
    max-width: none;
    width: 100%;
    border: none;
    border-radius: 0;
    background: transparent;
    box-shadow: none;
  }

  &--light {
    background: #f7f8fa;
    border-color: rgba(0, 0, 0, 0.08);
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.18), 0 1px 0 rgba(255, 255, 255, 0.6) inset;
    color: #1a1f29;

    &.punch-panel--inline {
      background: transparent;
      border: none;
      box-shadow: none;
    }

    .punch-panel__header {
      background: linear-gradient(180deg, rgba(0, 0, 0, 0.025), rgba(0, 0, 0, 0.005));
      color: #1a1f29;
      border-bottom-color: rgba(0, 0, 0, 0.06);
    }

    .punch-panel__summary-row {
      border-bottom-color: rgba(0, 0, 0, 0.06);
    }

    .punch-panel__summary-value--accent {
      color: color-mix(in srgb, var(--q-primary) 75%, #000);
    }

    .punch-panel__footer {
      color: #6b7280;
      border-top-color: rgba(0, 0, 0, 0.06);
    }

    .punch-panel__status-detail {
      color: #6b7280;

      &--elapsed {
        color: #1a1f29;
      }
    }

    .punch-panel__action-btn {
      @mixin tone-light($color) {
        background: color-mix(in srgb, $color 14%, #fff);
        border-color: color-mix(in srgb, $color 35%, transparent);
        color: color-mix(in srgb, $color 75%, #000);

        &:hover {
          background: color-mix(in srgb, $color 22%, #fff);
          border-color: color-mix(in srgb, $color 55%, transparent);
        }
      }

      &--positive { @include tone-light(var(--q-positive)); }
      &--negative { @include tone-light(var(--q-negative)); }
      &--warning { @include tone-light(var(--q-warning)); }
      &--info { @include tone-light(var(--q-info)); }
    }
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 10px 12px;
    background: linear-gradient(180deg, rgba(255, 255, 255, 0.04), rgba(255, 255, 255, 0.01));
    border-bottom: 1px solid rgba(255, 255, 255, 0.06);
    color: #f1f4f8;
  }

  &__header-title {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
  }

  &__header-icon {
    color: #4dd0e1;
    flex-shrink: 0;
  }

  &__title {
    font-size: 0.85rem;
    font-weight: 600;
    letter-spacing: 0.01em;
  }

  &__badge {
    font-size: 0.65rem;
    padding: 0 6px;
    height: 18px;
  }

  &__refresh {
    color: inherit;
    opacity: 0.75;

    &:hover {
      opacity: 1;
    }
  }

  &__body {
    padding: 10px 12px 8px;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  &__status-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    min-width: 0;
  }

  &__status-detail {
    font-size: 0.78rem;
    color: #8a93a2;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    min-width: 0;

    &--since {
      text-align: center;
    }

    &--elapsed {
      text-align: right;
      font-weight: 600;
      color: #cfd6e0;
    }
  }

  &__actions {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  &__action-btn {
    min-height: 38px;
    width: 100%;
    font-size: 0.82rem;
    font-weight: 600;
    letter-spacing: 0.01em;
    border-radius: 8px;
    border: 1px solid transparent;
    padding: 4px 10px;
    transition: background 0.15s, border-color 0.15s;

    // this style block is unscoped, so a plain descendant selector already
    // reaches the button content; :deep() outside scoped CSS is invalid and
    // the minifier rejects it
    .q-btn__content {
      flex-wrap: wrap;
      white-space: normal;
      text-align: center;
      line-height: 1.15;
      gap: 4px;
    }

    @mixin tone($color) {
      background: color-mix(in srgb, $color 16%, transparent);
      border-color: color-mix(in srgb, $color 30%, transparent);
      color: color-mix(in srgb, $color 35%, #fff);

      &:hover {
        background: color-mix(in srgb, $color 24%, transparent);
        border-color: color-mix(in srgb, $color 45%, transparent);
      }
    }

    &--positive { @include tone(var(--q-positive)); }
    &--negative { @include tone(var(--q-negative)); }
    &--warning { @include tone(var(--q-warning)); }
    &--info { @include tone(var(--q-info)); }
  }

  &__summary-list {
    margin: 0;
    padding: 4px 0;
    display: flex;
    flex-direction: column;
  }

  &__summary-row {
    display: grid;
    grid-template-columns: 28px 1fr 60px;
    align-items: baseline;
    gap: 12px;
    padding: 4px 2px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.04);

    &:last-child {
      border-bottom: none;
    }

    &--sub {
      padding: 2px 2px 2px 14px;
      border-bottom: none;
    }
  }

  &__summary-row > dt {
    grid-column: 1 / 3;
  }

  &__summary-row--sub > dt {
    grid-column: 1 / 2;
  }

  &__summary-row > dd {
    grid-column: 3 / 4;
    text-align: right;
  }

  &__summary-range {
    grid-column: 2 / 3;
    text-align: center;
    font-size: 0.72rem;
    font-variant-numeric: tabular-nums;
    opacity: 0.6;
  }

  &__summary-label {
    margin: 0;
    font-size: 0.7rem;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    opacity: 0.55;

    &--sub {
      text-transform: none;
      letter-spacing: 0;
      font-weight: 400;
      opacity: 0.45;
    }
  }

  &__summary-value {
    margin: 0;
    font-size: 0.85rem;
    font-weight: 600;
    font-variant-numeric: tabular-nums;

    &--accent {
      color: color-mix(in srgb, var(--q-primary) 30%, #fff);
    }

    &--sub {
      font-size: 0.78rem;
      font-weight: 500;
      opacity: 0.7;
    }
  }

  &__footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    font-size: 0.7rem;
    padding: 4px 8px 4px 12px;
    border-top: 1px solid rgba(255, 255, 255, 0.06);
  }

  &__footer-text {
    opacity: 0.55;
  }

  &__close {
    margin-left: 2px;
    opacity: 0.7;

    &:hover { opacity: 1; }
  }

  &__confirm-overlay {
    position: absolute;
    inset: 0;
    background: rgba(10, 14, 20, 0.78);
    backdrop-filter: blur(4px);
    -webkit-backdrop-filter: blur(4px);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px;
    z-index: 10;
  }

  &--light &__confirm-overlay {
    background: rgba(247, 248, 250, 0.85);
  }

  &__confirm-card {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    padding: 16px 14px 12px;
    border-radius: 12px;
    background: #1c2129;
    border: 1px solid rgba(255, 255, 255, 0.08);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
    width: 100%;
    max-width: 280px;

    @mixin confirm-tone($color) {
      border-color: color-mix(in srgb, $color 45%, transparent);

      .punch-panel__confirm-icon {
        color: color-mix(in srgb, $color 60%, #fff);
      }
    }

    &--positive { @include confirm-tone(var(--q-positive)); }
    &--negative { @include confirm-tone(var(--q-negative)); }
    &--warning { @include confirm-tone(var(--q-warning)); }
    &--info { @include confirm-tone(var(--q-info)); }
  }

  &--light &__confirm-card {
    background: #fff;
    border-color: rgba(0, 0, 0, 0.1);
    color: #1a1f29;
  }

  &__confirm-icon {
    opacity: 0.9;
  }

  &__confirm-title {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    font-size: 0.95rem;
    font-weight: 600;
    letter-spacing: 0.01em;
    line-height: 1.25;
  }

  &__confirm-buttons {
    display: flex;
    gap: 8px;
    width: 100%;
    margin-top: 4px;
  }

  &__confirm-spinner {
    display: flex;
    justify-content: center;
    padding: 6px 0 2px;
  }

  &__confirm-input {
    width: 100%;
  }

  &__confirm-btn {
    flex: 1 1 0;
    min-width: 0;

    &--cancel {
      opacity: 0.7;
    }
  }
}

.punch-panel-confirm-enter-active,
.punch-panel-confirm-leave-active {
  transition: opacity 0.15s ease;
}

.punch-panel-confirm-enter-from,
.punch-panel-confirm-leave-to {
  opacity: 0;
}
</style>
